import type { stats } from "@app/domain";
import type { Variant } from "@app/protocol";
import { useEffect, useState } from "react";
import { API_BASE } from "../config";
import { useT } from "../i18n/context";
import { useStore } from "../store";
import { Avatar } from "./Avatar";
import { useRankFrames } from "./useRankFrames";

interface Profile {
  stats: stats.PlayerStats;
  recent: stats.ProfileMatch[];
}

/** A player's totals from the server; null until loaded or when the server has none. */
export function useProfile(userId: string | null): Profile | null {
  const session = useStore((s) => s.session);
  const [profile, setProfile] = useState<Profile | null>(null);
  useEffect(() => {
    if (session === null || userId === null) return;
    let alive = true;
    fetch(`${API_BASE}/players/${encodeURIComponent(userId)}/stats`, {
      headers: { Authorization: `Bearer ${session}` },
    })
      .then((response) => (response.ok ? (response.json() as Promise<Profile>) : null))
      .then((body) => {
        if (alive && body !== null) setProfile(body);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [session, userId]);
  return profile;
}

/** Your avatar, name and record in the lobby header (design 2; review 0009 S2). */
export function ProfileChip({ variant, onOpen }: { variant: Variant; onOpen: () => void }) {
  const { t } = useT();
  const member = useStore((s) => s.member);
  // The game being set up decides the frame, as a table's game does (review 0014).
  const rank = useRankFrames(variant)(member);
  const profile = useProfile(member?.userId ?? null);
  if (member === null) return null;
  const totals = profile?.stats ?? null;
  const record =
    totals === null
      ? t("profile.loading")
      : totals.matches === 0
        ? t("profile.none")
        : t("profile.record", { matches: totals.matches, wins: totals.wins });
  return (
    <button
      type="button"
      aria-label={t("data.open", { name: member.name })}
      onClick={onOpen}
      className="flex min-w-0 items-center gap-3 rounded-pill border-[1.5px] border-ink/40 bg-panel-strong py-2 pl-2 pr-4 text-start shadow-float backdrop-blur-[10px]"
    >
      <Avatar member={member} size={rank === null ? 40 : 52} rank={rank} />
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-[14px] font-bold">{member.name}</span>
        <span className="truncate text-[12px] text-ink/75">{record}</span>
      </span>
    </button>
  );
}
